import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Box, Tab, Tabs, Tooltip } from '@mui/material';
import { useState } from 'react';
import type { CallMetricsByAgent } from '../../../interfaces/calls';
import { CustomTable } from '../../common/CustomTable';

// Helper to create column label with tooltip
const ColumnWithTooltip = ({ label, tooltip }: { label: string; tooltip: string }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
    {label}
    <Tooltip title={tooltip} arrow placement='top'>
      <InfoOutlinedIcon sx={{ fontSize: 16, color: 'action.active', cursor: 'help' }} />
    </Tooltip>
  </Box>
);

interface AgentsTabProps {
  agents: CallMetricsByAgent[];
  comparison?: CallMetricsByAgent[];
  formatWithComparisonTwoRows: (value: string | number, prevValue?: string | number) => JSX.Element;
  formatDuration: (seconds: number) => string;
  findAgentComparison: <T extends { agentId: number }>(
    agentId: number,
    comparisonAgents?: T[],
  ) => T | undefined;
}

export const AgentsTab = ({
  agents,
  comparison,
  formatWithComparisonTwoRows,
  formatDuration,
  findAgentComparison,
}: AgentsTabProps) => {
  const [detailTab, setDetailTab] = useState<'inbound' | 'outbound' | 'transfers'>('inbound');

  return (
    <Box>
      {/* Agent Overview */}
      <CustomTable
        title='Agent Overview'
        columns={[
          { key: 'agentName', label: 'Agent' },
          { key: 'teams', label: 'Teams' },
          { key: 'totalCalls', label: 'Total Calls' },
          { key: 'inbound', label: 'Inbound' },
          { key: 'outbound', label: 'Outbound' },
          { key: 'avgDuration', label: 'Avg Duration' },
          { key: 'avgWaitTime', label: 'Avg Wait (Inbound)' },
          { key: 'avgAnswerTime', label: 'Avg Answer (Outbound)' },
        ]}
        rows={agents.map((a) => {
          const prevData = findAgentComparison(a.agentId, comparison);

          return {
            agentName: a.agentName || 'Unknown',
            teams: a.teams || 'N/A',
            totalCalls: formatWithComparisonTwoRows(a.count, prevData?.count),
            inbound: formatWithComparisonTwoRows(a.inboundCount, prevData?.inboundCount),
            outbound: formatWithComparisonTwoRows(a.outboundCount, prevData?.outboundCount),
            avgDuration: formatWithComparisonTwoRows(
              a.averageDuration ? formatDuration(a.averageDuration) : 'N/A',
              prevData?.averageDuration ? formatDuration(prevData.averageDuration) : undefined,
            ),
            avgWaitTime: formatWithComparisonTwoRows(
              a.averageWaitTime ? formatDuration(a.averageWaitTime) : 'N/A',
              prevData?.averageWaitTime ? formatDuration(prevData.averageWaitTime) : undefined,
            ),
            avgAnswerTime: formatWithComparisonTwoRows(
              a.averageAnswerTime ? formatDuration(a.averageAnswerTime) : 'N/A',
              prevData?.averageAnswerTime ? formatDuration(prevData.averageAnswerTime) : undefined,
            ),
          };
        })}
        defaultText='No agent data available'
        loading={false}
        exportable={true}
        exportFilename='calls-agent-overview.csv'
      />

      {/* Detailed Performance - with mini tabs */}
      <Box sx={{ mt: 3 }}>
        <Tabs value={detailTab} onChange={(_, newValue) => setDetailTab(newValue)}>
          <Tab label='📞 Inbound' value='inbound' />
          <Tab label='📤 Outbound' value='outbound' />
          <Tab label='🔄 Transfers' value='transfers' />
        </Tabs>

        <Box sx={{ mt: 2 }}>
          {/* Inbound Performance */}
          {detailTab === 'inbound' && (
            <>
              <CustomTable
                title='Agent Inbound Performance'
                columns={[
                  { key: 'agentName', label: 'Agent' },
                  { key: 'teams', label: 'Teams' },
                  { key: 'answered', label: 'Answered' },
                  { key: 'answerRate', label: 'Answer Rate' },
                  {
                    key: 'abandoned',
                    label: (
                      <ColumnWithTooltip
                        label='Abandoned'
                        tooltip='Calls where the client hung up before 30 seconds of wait time'
                      />
                    ),
                  },
                  { key: 'abandonedRate', label: 'Abandoned Rate' },
                  {
                    key: 'missed',
                    label: (
                      <ColumnWithTooltip
                        label='Missed'
                        tooltip='Calls not answered because no agent was available'
                      />
                    ),
                  },
                  { key: 'missedRate', label: 'Missed Rate' },
                  {
                    key: 'rejected',
                    label: (
                      <ColumnWithTooltip
                        label='Rejected'
                        tooltip='Calls that an agent actively declined to answer'
                      />
                    ),
                  },
                  { key: 'rejectedRate', label: 'Rejected Rate' },
                ]}
                rows={agents.map((a) => {
                  const prevData = findAgentComparison(a.agentId, comparison);

                  const answered = a.inboundDetails?.answered ?? a.answeredCalls ?? 0;
                  const abandoned = a.inboundDetails?.abandoned ?? 0;
                  const missed = a.inboundDetails?.missed ?? a.missedCalls ?? 0;
                  const rejected = a.inboundDetails?.rejected ?? 0;
                  const answerRate = a.inboundDetails?.answerRate ?? 0;

                  // Calculate rates
                  const totalInbound = answered + abandoned + missed + rejected;
                  const abandonedRate = totalInbound > 0 ? (abandoned / totalInbound) * 100 : 0;
                  const missedRate = totalInbound > 0 ? (missed / totalInbound) * 100 : 0;
                  const rejectedRate = totalInbound > 0 ? (rejected / totalInbound) * 100 : 0;

                  const prevAnswered =
                    prevData?.inboundDetails?.answered ?? prevData?.answeredCalls;
                  const prevAbandoned = prevData?.inboundDetails?.abandoned;
                  const prevMissed = prevData?.inboundDetails?.missed ?? prevData?.missedCalls;
                  const prevRejected = prevData?.inboundDetails?.rejected;
                  const prevAnswerRate = prevData?.inboundDetails?.answerRate;

                  const prevTotalInbound =
                    (prevAnswered ?? 0) +
                    (prevAbandoned ?? 0) +
                    (prevMissed ?? 0) +
                    (prevRejected ?? 0);
                  const prevAbandonedRate =
                    prevTotalInbound > 0 ? ((prevAbandoned ?? 0) / prevTotalInbound) * 100 : 0;
                  const prevMissedRate =
                    prevTotalInbound > 0 ? ((prevMissed ?? 0) / prevTotalInbound) * 100 : 0;
                  const prevRejectedRate =
                    prevTotalInbound > 0 ? ((prevRejected ?? 0) / prevTotalInbound) * 100 : 0;

                  return {
                    agentName: a.agentName || 'Unknown',
                    teams: a.teams || 'N/A',
                    answered: formatWithComparisonTwoRows(answered, prevAnswered),
                    answerRate: formatWithComparisonTwoRows(
                      `${answerRate.toFixed(1)}%`,
                      prevAnswerRate !== undefined ? `${prevAnswerRate.toFixed(1)}%` : undefined,
                    ),
                    abandoned: formatWithComparisonTwoRows(abandoned, prevAbandoned),
                    abandonedRate: formatWithComparisonTwoRows(
                      `${abandonedRate.toFixed(1)}%`,
                      prevTotalInbound > 0 ? `${prevAbandonedRate.toFixed(1)}%` : undefined,
                    ),
                    missed: formatWithComparisonTwoRows(missed, prevMissed),
                    missedRate: formatWithComparisonTwoRows(
                      `${missedRate.toFixed(1)}%`,
                      prevTotalInbound > 0 ? `${prevMissedRate.toFixed(1)}%` : undefined,
                    ),
                    rejected: formatWithComparisonTwoRows(rejected, prevRejected),
                    rejectedRate: formatWithComparisonTwoRows(
                      `${rejectedRate.toFixed(1)}%`,
                      prevTotalInbound > 0 ? `${prevRejectedRate.toFixed(1)}%` : undefined,
                    ),
                  };
                })}
                defaultText='No agent data available'
                loading={false}
                exportable={true}
                exportFilename='calls-agent-inbound-performance.csv'
              />
            </>
          )}

          {/* Outbound Performance */}
          {detailTab === 'outbound' && (
            <>
              <CustomTable
                title='Agent Outbound Performance'
                columns={[
                  { key: 'agentName', label: 'Agent' },
                  { key: 'teams', label: 'Teams' },
                  { key: 'completed', label: 'Completed' },
                  { key: 'successRate', label: 'Success Rate' },
                  { key: 'failed', label: 'Failed' },
                  { key: 'failureRate', label: 'Failure Rate' },
                ]}
                rows={agents.map((a) => {
                  const prevData = findAgentComparison(a.agentId, comparison);

                  const completed = a.outboundDetails?.completed ?? 0;
                  const failed = a.outboundDetails?.failed ?? 0;
                  const successRate = a.outboundDetails?.successRate ?? 0;

                  // Calculate failure rate
                  const totalOutbound = completed + failed;
                  const failureRate = totalOutbound > 0 ? (failed / totalOutbound) * 100 : 0;

                  const prevCompleted = prevData?.outboundDetails?.completed;
                  const prevFailed = prevData?.outboundDetails?.failed;
                  const prevSuccessRate = prevData?.outboundDetails?.successRate;

                  const prevTotalOutbound = (prevCompleted ?? 0) + (prevFailed ?? 0);
                  const prevFailureRate =
                    prevTotalOutbound > 0 ? ((prevFailed ?? 0) / prevTotalOutbound) * 100 : 0;

                  return {
                    agentName: a.agentName || 'Unknown',
                    teams: a.teams || 'N/A',
                    completed: formatWithComparisonTwoRows(completed, prevCompleted),
                    successRate: formatWithComparisonTwoRows(
                      `${successRate.toFixed(1)}%`,
                      prevSuccessRate !== undefined ? `${prevSuccessRate.toFixed(1)}%` : undefined,
                    ),
                    failed: formatWithComparisonTwoRows(failed, prevFailed),
                    failureRate: formatWithComparisonTwoRows(
                      `${failureRate.toFixed(1)}%`,
                      prevTotalOutbound > 0 ? `${prevFailureRate.toFixed(1)}%` : undefined,
                    ),
                  };
                })}
                defaultText='No agent data available'
                loading={false}
                exportable={true}
                exportFilename='calls-agent-outbound-performance.csv'
              />
            </>
          )}

          {/* Transfers */}
          {detailTab === 'transfers' && (
            <>
              <CustomTable
                title='Agent Transfer Performance'
                columns={[
                  { key: 'agentName', label: 'Agent' },
                  { key: 'teams', label: 'Teams' },
                  { key: 'transfersInitiated', label: 'Initiated' },
                  { key: 'transfersReceived', label: 'Received' },
                  { key: 'totalTransfers', label: 'Total' },
                ]}
                rows={agents.map((a) => {
                  const prevData = findAgentComparison(a.agentId, comparison);

                  const transfersInitiated = a.transfersInitiated ?? 0;
                  const transfersReceived = a.transfersReceived ?? 0;
                  const totalTransfers = transfersInitiated + transfersReceived;

                  const prevTransfersInitiated = prevData?.transfersInitiated;
                  const prevTransfersReceived = prevData?.transfersReceived;
                  const prevTotalTransfers =
                    prevTransfersInitiated && prevTransfersReceived
                      ? prevTransfersInitiated + prevTransfersReceived
                      : undefined;

                  return {
                    agentName: a.agentName || 'Unknown',
                    teams: a.teams || '-',
                    transfersInitiated: formatWithComparisonTwoRows(
                      transfersInitiated,
                      prevTransfersInitiated,
                    ),
                    transfersReceived: formatWithComparisonTwoRows(
                      transfersReceived,
                      prevTransfersReceived,
                    ),
                    totalTransfers: formatWithComparisonTwoRows(totalTransfers, prevTotalTransfers),
                  };
                })}
                defaultText='No transfer data available'
                loading={false}
                exportable={true}
                exportFilename='calls-agent-transfer-performance.csv'
              />
            </>
          )}
        </Box>
      </Box>

      {/* Agent Rating Performance */}
      <Box sx={{ mt: 3 }}>
        <CustomTable
          title='Agent Rating Performance'
          columns={[
            { key: 'agentName', label: 'Agent' },
            { key: 'teams', label: 'Teams' },
            { key: 'evaluatedCalls', label: 'Evaluated' },
            { key: 'unevaluatedCalls', label: 'Unevaluated' },
            { key: 'evaluationRate', label: 'Evaluation Rate' },
            { key: 'avgRating', label: 'Avg Rating' },
            { key: 'positiveRate', label: 'Positive Rate (≥4)' },
          ]}
          rows={agents.map((a) => {
            const prevData = findAgentComparison(a.agentId, comparison);

            const evaluatedCalls = a.ratingMetrics?.evaluatedCalls ?? 0;
            const unevaluatedCalls = a.ratingMetrics?.unevaluatedCalls ?? 0;
            const avgRating = a.ratingMetrics?.averageRating ?? 0;
            const positiveRate = a.ratingMetrics?.positiveRatingRate ?? 0;

            const totalCalls = evaluatedCalls + unevaluatedCalls;
            const evaluationRate = totalCalls > 0 ? (evaluatedCalls / totalCalls) * 100 : 0;

            const prevEvaluatedCalls = prevData?.ratingMetrics?.evaluatedCalls;
            const prevUnevaluatedCalls = prevData?.ratingMetrics?.unevaluatedCalls;
            const prevAvgRating = prevData?.ratingMetrics?.averageRating;
            const prevPositiveRate = prevData?.ratingMetrics?.positiveRatingRate;

            const prevTotalCalls = (prevEvaluatedCalls ?? 0) + (prevUnevaluatedCalls ?? 0);
            const prevEvaluationRate =
              prevTotalCalls > 0 ? ((prevEvaluatedCalls ?? 0) / prevTotalCalls) * 100 : 0;

            return {
              agentName: a.agentName || 'Unknown',
              teams: a.teams || 'N/A',
              evaluatedCalls: formatWithComparisonTwoRows(evaluatedCalls, prevEvaluatedCalls),
              unevaluatedCalls: formatWithComparisonTwoRows(unevaluatedCalls, prevUnevaluatedCalls),
              evaluationRate: formatWithComparisonTwoRows(
                `${evaluationRate.toFixed(1)}%`,
                prevTotalCalls > 0 ? `${prevEvaluationRate.toFixed(1)}%` : undefined,
              ),
              avgRating: formatWithComparisonTwoRows(
                avgRating > 0 ? avgRating.toFixed(2) : 'N/A',
                prevAvgRating && prevAvgRating > 0 ? prevAvgRating.toFixed(2) : undefined,
              ),
              positiveRate: formatWithComparisonTwoRows(
                positiveRate > 0 ? `${positiveRate.toFixed(1)}%` : 'N/A',
                prevPositiveRate && prevPositiveRate > 0
                  ? `${prevPositiveRate.toFixed(1)}%`
                  : undefined,
              ),
            };
          })}
          defaultText='No rating data available'
          loading={false}
          exportable={true}
          exportFilename='calls-agent-rating-performance.csv'
        />
      </Box>
    </Box>
  );
};
